import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { createHandymanSocialSharing } from '../../util/handymanSocialSharing';
import { buildHandymanLocalBusinessSchema } from '../../util/handymanLocalBusinessSchema';
import { propTypes } from '../../util/types';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import CleaningListingsSection from '../CleaningPage/CleaningListingsSection';
import { createCleaningMapStateToProps } from '../CleaningPage/cleaningPageState';
import { OPEN_QUOTE_EVENT } from '../../components/QuoteChat/QuoteChat';
import css from '../HandymanPage/HandymanPage.module.css';

// Open the AI intake chat, optionally seeding the first message with the page's
// service + city so the visitor lands in a context-aware conversation.
const openQuoteChat = prefill => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_QUOTE_EVENT, { detail: prefill ? { prefill } : {} }));
  }
};

import {
  getCleaningCityPageData,
  cleaningCityLinks,
  torontoCleaningServiceLinks,
} from '../../data/cleaningSeoPages';
import { linkifyCleaningServices } from '../../data/cleaningServiceLinkifier';

const HERO_IMAGE = '/static/services/cleaning/cleaning-hero-vacuum-bathroom.jpg';

export const CleaningCityPageComponent = props => {
  const { listings, params, queryInProgress, queryListingsError } = props;
  const cityConfig = getCleaningCityPageData(params?.citySlug);
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://fixittask.com';

  if (!cityConfig) {
    return <NotFoundPage />;
  }

  const browseByCityLinks = cleaningCityLinks
    .filter(item => item.slug !== cityConfig.slug)
    .map(item => ({ ...item, href: item.path }));
  const createSearchPath = searchParams =>
    createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);
  // "See more cleaners" should land on the search page pre-filtered to cleaning.
  const browseAllPath = createSearchPath({ pub_categoryLevel1: 'Cleaning_Services' });

  // Seed the chat with the page's service + city so the CTA opens a
  // context-aware conversation instead of a blank one.
  const quotePrefill = `I need cleaning in ${cityConfig.city}`;

  // When there are no live listings yet, don't send the visitor to another
  // empty page — invite them straight into the chat so we can match them
  // manually and capture the lead.
  const listingsEmptyState = (
    <div className={css.listingsState}>
      <h3 className={css.cardTitle}>Tell us what you need — we’ll find you a cleaner</h3>
      <p className={css.cardText}>
        New cleaners join every week. Describe the job (a photo helps) and we’ll match you with a
        trusted local pro in {cityConfig.city} and call you back shortly.
      </p>
      <button type="button" className={css.quoteButton} onClick={() => openQuoteChat(quotePrefill)}>
        Get a free quote
      </button>
    </div>
  );

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: cityConfig.h1,
      description: cityConfig.metaDescription,
      url: `${rootUrl}${cityConfig.path}`,
    },
    buildHandymanLocalBusinessSchema({
      rootUrl,
      pageUrl: `${rootUrl}${cityConfig.path}`,
      name: cityConfig.h1,
      description: cityConfig.metaDescription,
      image: `${rootUrl}${HERO_IMAGE}`,
      city: cityConfig.city,
      region: 'Ontario',
    }),
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cleaning',
          item: `${rootUrl}/cleaning`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: cityConfig.city,
          item: `${rootUrl}${cityConfig.path}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: cityConfig.faqs.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <Page
      title={cityConfig.metaTitle}
      description={cityConfig.metaDescription}
      socialSharing={createHandymanSocialSharing(
        rootUrl,
        cityConfig.metaTitle,
        cityConfig.metaDescription
      )}
      schema={schema}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={`${rootUrl}${cityConfig.path}`} />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <section className={css.hero} style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
            <div className={css.heroOverlay}>
              <div className={css.heroContent}>
                <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                  <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                    Home
                  </NamedLink>
                  <span aria-hidden="true">/</span>
                  <a href="/cleaning" className={css.breadcrumbLink}>
                    Cleaning
                  </a>
                  <span aria-hidden="true">/</span>
                  <span>{cityConfig.city}</span>
                </nav>

                <p className={css.eyebrow}>{cityConfig.eyebrow}</p>
                <h1 className={css.title}>{cityConfig.h1}</h1>
                <p className={css.lead}>{cityConfig.lead}</p>

                <div className={css.heroCta}>
                  <button
                    type="button"
                    className={css.quoteButton}
                    onClick={() => openQuoteChat(quotePrefill)}
                  >
                    Get a free quote
                  </button>
                  <ul className={css.trustRow}>
                    <li>Reply in minutes</li>
                    <li>Vetted local cleaners</li>
                    <li>No sign-up, no obligation</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <CleaningListingsSection
            heading={cityConfig.listingHeading}
            text={cityConfig.listingText}
            listings={listings}
            queryInProgress={queryInProgress}
            queryListingsError={queryListingsError}
            browseAllPath={browseAllPath}
            emptyStateChildren={listingsEmptyState}
          />

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>Booking guide</p>
              <h2 className={css.sectionTitle}>{cityConfig.bookingHeading}</h2>
              <p className={css.sectionText}>{cityConfig.bookingText}</p>
            </div>

            <div className={css.contentGrid}>
              {cityConfig.contentSections.map(section => (
                <article className={css.contentCard} key={section.title}>
                  <h3 className={css.contentTitle}>{section.title}</h3>
                  {section.paragraphs.map(paragraph => (
                    <p className={css.contentParagraph} key={paragraph}>
                      {linkifyCleaningServices(paragraph, null)}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </section>

          {cityConfig.slug === 'toronto' ? (
            <section className={css.section}>
              <div className={css.sectionHeader}>
                <p className={css.sectionEyebrow}>
                  {cityConfig.serviceLinksEyebrow || 'Popular services'}
                </p>
                <h2 className={css.sectionTitle}>
                  {cityConfig.serviceLinksTitle || 'Popular cleaning services'}
                </h2>
                <p className={css.sectionText}>
                  {cityConfig.serviceLinksText ||
                    'These detailed service pages are currently available for Toronto and can still help you explore the cleaning jobs customers ask for most often.'}
                </p>
              </div>

              <div className={css.pillGrid}>
                {torontoCleaningServiceLinks.map(item => (
                  <a className={css.pillLink} href={item.href} key={item.label}>
                    {item.label}
                  </a>
                ))}
              </div>
            </section>
          ) : (
            <section className={css.section}>
              <div className={css.sectionHeader}>
                <p className={css.sectionEyebrow}>Popular services</p>
                <h2 className={css.sectionTitle}>Popular cleaning services</h2>
                <p className={css.sectionText}>
                  These detailed service pages are currently available for Toronto and can still
                  help you explore the cleaning jobs customers ask for most often.
                </p>
              </div>

              <div className={css.pillGrid}>
                {torontoCleaningServiceLinks.map(item => (
                  <a className={css.pillLink} href={item.href} key={item.label}>
                    {item.label}
                  </a>
                ))}
              </div>
            </section>
          )}

          <section className={css.section}>
            <div className={css.cityPanel}>
              <div className={css.sectionHeader}>
                <p className={css.sectionEyebrow}>Popular Ontario cities</p>
                <h2 className={css.sectionTitle}>Browse cleaning help by city</h2>
                <p className={css.sectionText}>
                  Open another city page if the project is outside {cityConfig.city} or if you want
                  to compare nearby service areas.
                </p>
              </div>

              <div className={css.cityGrid}>
                {browseByCityLinks.map(item => (
                  <a className={css.cityLink} href={item.href} key={item.slug}>
                    <span>{item.city}</span>
                    <span className={css.cityAction}>Browse</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.sectionEyebrow}>FAQ</p>
              <h2 className={css.sectionTitle}>
                Questions customers usually have in {cityConfig.city}
              </h2>
            </div>

            <div className={css.faqGrid}>
              {cityConfig.faqs.map(item => (
                <article className={css.faqCard} key={item.question}>
                  <h3 className={css.cardTitle}>{item.question}</h3>
                  <p className={css.cardText}>{linkifyCleaningServices(item.answer, null)}</p>
                </article>
              ))}
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

CleaningCityPageComponent.defaultProps = {
  listings: [],
  queryListingsError: null,
};

const { arrayOf, bool, shape, string } = PropTypes;

CleaningCityPageComponent.propTypes = {
  listings: arrayOf(propTypes.listing),
  params: shape({
    citySlug: string,
  }).isRequired,
  queryInProgress: bool.isRequired,
  queryListingsError: propTypes.error,
};

const CleaningCityPage = compose(connect(createCleaningMapStateToProps('CleaningCityPage')))(
  CleaningCityPageComponent
);

export default CleaningCityPage;
